import { useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, ArrowRightIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import Footer from "../../components/landing/Footer";
import LandingNavbar from "../../components/landing/LandingNavbar";
import usePageSeo from "../../hooks/usePageSeo";
import JsonLd from "../../components/seo/JsonLd";
import { Spinner } from "../../components/ui/Spinner";
import MarkdownArticle from "../../components/docs/MarkdownArticle";
import DocsSidebar from "../../components/docs/DocsSidebar";
import DocsToc from "../../components/docs/DocsToc";
import { useDocMarkdown } from "../../components/docs/useDocMarkdown";
import { useDocsPrintMode } from "../../components/docs/usePrintMode";
import { extractHeadings } from "../../components/docs/markdownHeadings";
import { DOC_SECTIONS, TIER_META, docPath, getDocSection, readingMinutes, type DocTier } from "../../content/docs/docsManifest";
import { marketingOrigin } from "../../data/seo/siteEntity";
import { comOnlyInboundGuideLink } from "../../data/seo/inboundVoiceGuide";
import { comMarketing } from "../../data/seo/kw3OnPage";
import "../../styles/docs-print.css";

type DocsArticlePageProps = {
    tier: DocTier;
};

export default function DocsArticlePage({ tier }: DocsArticlePageProps) {
    const { slug } = useParams<{ slug: string }>();
    const section = getDocSection(slug);
    const print = useDocsPrintMode();
    const { markdown, loading, error } = useDocMarkdown(section, tier);

    const tierMeta = TIER_META[tier];
    const otherTier: DocTier = tier === "quickstart" ? "manual" : "quickstart";

    const headings = useMemo(() => (markdown ? extractHeadings(markdown) : []), [markdown]);
    const minutes = useMemo(() => (markdown ? readingMinutes(markdown) : null), [markdown]);

    const index = section ? DOC_SECTIONS.findIndex((candidate) => candidate.slug === section.slug) : -1;
    const previous = index > 0 ? DOC_SECTIONS[index - 1] : null;
    const next = index >= 0 && index < DOC_SECTIONS.length - 1 ? DOC_SECTIONS[index + 1] : null;

    const pageTitle = section ? `${section.title} — ${tierMeta.label} | FloGPT Docs` : "FloGPT Docs";
    const pageDescription = section ? `${tierMeta.label}: ${section.summary}` : undefined;
    const canonical = section ? docPath(tier, section.slug) : "/docs";

    usePageSeo({
        title: pageTitle,
        description: pageDescription,
        canonical,
        ogType: "article",
        // `?print=1` is the PDF builder's input, not a public URL.
        noindex: print,
    });

    const inboundSetupGuide =
        section?.slug === "ai-calls" && tier === "quickstart" ? comOnlyInboundGuideLink() : null;

    const techArticleSchema = useMemo(() => {
        if (!section) return null;
        return {
            "@context": "https://schema.org",
            "@type": "TechArticle",
            headline: `${section.title} — ${tierMeta.label}`,
            description: section.summary,
            url: `${marketingOrigin()}${docPath(tier, section.slug)}`,
            inLanguage: "en",
            proficiencyLevel: tier === "quickstart" ? "Beginner" : "Expert",
            audience: { "@type": "Audience", audienceType: "FloGPT customers" },
            isPartOf: { "@type": "WebPage", url: `${marketingOrigin()}/docs` },
        };
    }, [section, tier, tierMeta.label]);

    if (!section) return <Navigate to="/docs" replace />;

    const body = (
        <>
            {loading ? (
                <div className="flex justify-center py-20">
                    <Spinner />
                </div>
            ) : null}

            {error ? (
                <div className="rounded-xl border border-red-900/50 bg-red-950/30 p-5 text-sm text-red-200">
                    This guide could not be loaded. Please refresh, or{" "}
                    <Link to="/contact" className="underline">
                        let us know
                    </Link>
                    .
                </div>
            ) : null}

            {markdown ? <MarkdownArticle markdown={markdown} print={print} /> : null}
        </>
    );

    // The PDF build loads this route with `?print=1`; Puppeteer rasterises the
    // on-screen layout, so the chrome has to be absent from the DOM rather
    // than merely hidden.
    if (print) {
        return (
            <div className="docs-print-mode min-h-screen bg-white">
                <div className="mx-auto max-w-3xl px-8 py-10">
                    <div className="mb-6 border-b border-slate-300 pb-4">
                        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{tierMeta.label}</div>
                        <h1 className="mt-1 text-2xl font-bold text-black">{section.title}</h1>
                        <p className="mt-1 text-sm text-slate-600">{section.summary}</p>
                    </div>
                    {body}
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-950 text-slate-100">
            <LandingNavbar />

            <div className="mx-auto max-w-7xl px-6 pb-20 pt-24 lg:px-8 sm:pt-28">
                <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-1.5 text-sm text-slate-500">
                    <Link to="/docs" className="hover:text-slate-300">
                        Docs
                    </Link>
                    <ChevronRightIcon className="h-3.5 w-3.5" />
                    <span className="text-slate-400">{tierMeta.label}</span>
                    <ChevronRightIcon className="h-3.5 w-3.5" />
                    <span className="text-slate-300">{section.title}</span>
                </nav>

                <div className="grid gap-10 lg:grid-cols-[15rem_minmax(0,1fr)_13rem]">
                    <aside className="lg:sticky lg:top-24 lg:h-[calc(100vh-8rem)] lg:overflow-y-auto lg:pr-2">
                        <DocsSidebar activeSlug={section.slug} activeTier={tier} />
                    </aside>

                    <article className="min-w-0">
                        <header className="mb-8 border-b border-slate-800 pb-6">
                            <div className="text-xs font-semibold uppercase tracking-wider text-orange-400">{tierMeta.label}</div>
                            <h1 className="mt-2 text-3xl font-bold text-white sm:text-4xl">{section.title}</h1>
                            <p className="mt-3 text-lg leading-relaxed text-slate-300">{section.summary}</p>
                            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500">
                                {minutes ? <span>{minutes} min read</span> : null}
                                {section.navLabel ? (
                                    <span>
                                        Sidebar: <span className="text-slate-400">{section.navLabel}</span>
                                    </span>
                                ) : null}
                                <Link to={docPath(otherTier, section.slug)} className="text-orange-400 hover:text-orange-300">
                                    Switch to {TIER_META[otherTier].label}
                                </Link>
                            </div>
                            {section.roleNote ? (
                                <div className="mt-4 rounded-lg border border-slate-800 bg-slate-900/50 px-4 py-2.5 text-sm text-slate-400">
                                    <span className="font-medium text-slate-300">Who can see this: </span>
                                    {section.roleNote}
                                </div>
                            ) : null}
                            {section.status === "outline" ? (
                                <div className="mt-3 rounded-lg border border-amber-900/50 bg-amber-950/20 px-4 py-2.5 text-sm text-amber-200/80">
                                    This section is an outline — the headings are final, the prose is still being written.
                                </div>
                            ) : null}
                        </header>

                        {body}

                        {comMarketing() && tier === "quickstart" && section.slug === "integrations" ? (
                            <p className="mt-10 text-base text-slate-300">
                                Carriers that answer tracking and delivery calls can start from the{" "}
                                <Link to="/use-cases/logistics" className="font-medium text-orange-400 hover:text-orange-300">
                                    logistics voice API
                                </Link>
                                .
                            </p>
                        ) : null}

                        {inboundSetupGuide ? (
                            <p className="mt-10 text-base text-slate-300">
                                <Link to={inboundSetupGuide.href} className="font-medium text-orange-400 hover:text-orange-300">
                                    {inboundSetupGuide.label}
                                </Link>
                            </p>
                        ) : null}

                        <nav className="mt-14 grid gap-3 border-t border-slate-800 pt-6 sm:grid-cols-2">
                            {previous ? (
                                <Link
                                    to={docPath(tier, previous.slug)}
                                    className="group flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/40 p-4 transition hover:border-slate-700"
                                >
                                    <ArrowLeftIcon className="h-4 w-4 shrink-0 text-slate-500 group-hover:text-orange-400" />
                                    <span className="min-w-0">
                                        <span className="block text-xs text-slate-500">Previous</span>
                                        <span className="block truncate font-medium text-slate-200">{previous.title}</span>
                                    </span>
                                </Link>
                            ) : (
                                <span />
                            )}
                            {next ? (
                                <Link
                                    to={docPath(tier, next.slug)}
                                    className="group flex items-center justify-end gap-3 rounded-xl border border-slate-800 bg-slate-900/40 p-4 text-right transition hover:border-slate-700"
                                >
                                    <span className="min-w-0">
                                        <span className="block text-xs text-slate-500">Next</span>
                                        <span className="block truncate font-medium text-slate-200">{next.title}</span>
                                    </span>
                                    <ArrowRightIcon className="h-4 w-4 shrink-0 text-slate-500 group-hover:text-orange-400" />
                                </Link>
                            ) : null}
                        </nav>
                    </article>

                    <aside className="hidden lg:sticky lg:top-24 lg:block lg:h-[calc(100vh-8rem)] lg:overflow-y-auto">
                        <DocsToc headings={headings} />
                    </aside>
                </div>
            </div>

            <Footer />
            {techArticleSchema ? <JsonLd id={`techarticle-docs-${tier}-${section.slug}`} data={techArticleSchema} /> : null}
        </div>
    );
}
